import { useState } from 'react';
import type {
  Appointment,
  AvailabilitySlot,
  AppointmentType,
} from '../../../../mocks/appointments';
import { dayNames } from '../../../../mocks/appointments';
import { timeToMinutes } from '../../../../utils/occupancy';

interface Props {
  slots: AvailabilitySlot[];
  appointments: Appointment[];
  types: AppointmentType[];
  addSlot: (
    s: Omit<AvailabilitySlot, 'id' | 'created_at'>
  ) => Promise<AvailabilitySlot | null>;
  updateSlot: (
    id: string,
    u: Partial<AvailabilitySlot>
  ) => Promise<AvailabilitySlot | null>;
  deleteSlot: (id: string) => Promise<boolean>;
}

export default function AvailabilityManager({
  slots,
  appointments,
  types,
  addSlot,
  updateSlot,
  deleteSlot,
}: Props) {
  const [day, setDay] = useState(1);
  const [start, setStart] = useState('10:00');
  const [end, setEnd] = useState('13:00');
  const [max, setMax] = useState(1);
  const [editingSlot, setEditingSlot] = useState<AvailabilitySlot | null>(null);
  const [editDay, setEditDay] = useState(1);
  const [editStart, setEditStart] = useState('10:00');
  const [editEnd, setEditEnd] = useState('13:00');
  const [editMax, setEditMax] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showDeleteSlot, setShowDeleteSlot] = useState<string | null>(null);
  const [dayFilter, setDayFilter] = useState<number | 'all'>('all');
  const [timeFilter, setTimeFilter] = useState('');

  const openEdit = (slot: AvailabilitySlot) => {
    setEditingSlot(slot);
    setEditDay(slot.day_of_week);
    setEditStart(slot.start_time);
    setEditEnd(slot.end_time);
    setEditMax(slot.max_bookings);
  };

  const handleSaveEdit = async () => {
    if (!editingSlot) return;
    setSaving(true);
    await updateSlot(editingSlot.id, {
      day_of_week: editDay,
      start_time: editStart,
      end_time: editEnd,
      max_bookings: editMax,
    });
    setSaving(false);
    setEditingSlot(null);
  };

  const handleAdd = async () => {
    await addSlot({
      day_of_week: day,
      start_time: start,
      end_time: end,
      is_active: true,
      max_bookings: max,
    });
    setDay(1);
    setStart('10:00');
    setEnd('13:00');
    setMax(1);
  };

  const getSlotUsage = (slot: AvailabilitySlot) => {
    const usageByDate = new Map<string, number>();
    appointments
      .filter((a) => a.status !== 'cancelled')
      .forEach((appt) => {
        const d = new Date(appt.preferred_date);
        if (d.getDay() !== slot.day_of_week) return;
        if (!appt.preferred_time) return;
        const type = types.find((t) => t.name === appt.service_type);
        const duration = type?.duration_minutes || 30;
        const apptStart = timeToMinutes(appt.preferred_time);
        const apptEnd = apptStart + duration;
        const slotStart = timeToMinutes(slot.start_time);
        const slotEnd = timeToMinutes(slot.end_time);
        if (apptStart < slotEnd && apptEnd > slotStart) {
          usageByDate.set(
            appt.preferred_date,
            (usageByDate.get(appt.preferred_date) || 0) + 1
          );
        }
      });
    return usageByDate.size > 0
      ? Math.max(...Array.from(usageByDate.values()))
      : 0;
  };

  const activeSlots = slots.filter((s) => s.is_active);
  const inactiveSlots = slots.filter((s) => !s.is_active);
  const filteredActiveSlots = activeSlots.filter((slot) => {
    if (dayFilter !== 'all' && slot.day_of_week !== dayFilter) return false;
    if (timeFilter.trim()) {
      const q = timeFilter.trim().toLowerCase();
      if (
        !`${slot.start_time} - ${slot.end_time}`.toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <h2 className="font-serif text-brand-black text-2xl font-medium">
        Horarios de disponibilidad
      </h2>
      <div className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Día
            </label>
            <select
              value={day}
              onChange={(e) => setDay(Number(e.target.value))}
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm bg-white"
            >
              {dayNames.map((d, i) => (
                <option key={i} value={i}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Inicio
            </label>
            <input
              type="time"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm"
            />
          </div>
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Fin
            </label>
            <input
              type="time"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm"
            />
          </div>
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Max. citas
            </label>
            <input
              type="number"
              min={1}
              max={10}
              value={max}
              onChange={(e) => setMax(Number(e.target.value))}
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm"
            />
          </div>
        </div>
        <button
          onClick={handleAdd}
          className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
        >
          + Añadir franja
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
        <span className="font-sans text-brand-gray-500 text-xs uppercase tracking-wider font-medium">
          Filtrar:
        </span>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setDayFilter('all')}
            className={`px-3 py-1.5 rounded-full font-sans text-xs transition-colors whitespace-nowrap ${
              dayFilter === 'all'
                ? 'bg-brand-black text-white'
                : 'bg-white border border-brand-gray-200 text-brand-gray-600 hover:text-brand-black'
            }`}
          >
            Todos
          </button>
          {dayNames.map((d, i) => (
            <button
              key={i}
              onClick={() => setDayFilter(i)}
              className={`px-3 py-1.5 rounded-full font-sans text-xs transition-colors whitespace-nowrap ${
                dayFilter === i
                  ? 'bg-brand-black text-white'
                  : 'bg-white border border-brand-gray-200 text-brand-gray-600 hover:text-brand-black'
              }`}
            >
              {d}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <input
            type="text"
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            placeholder="Buscar hora, ej: 10:00"
            className="w-48 bg-white border border-brand-gray-200 rounded-lg px-3 py-1.5 font-sans text-sm focus:outline-none focus:border-brand-black"
          />
          {timeFilter && (
            <button
              onClick={() => setTimeFilter('')}
              className="w-7 h-7 rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-gray-500 hover:text-brand-black transition-colors"
              title="Limpiar"
            >
              <i className="ri-close-line text-xs" />
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 bg-brand-gray-50 border-b border-brand-gray-200">
          <h3 className="font-sans text-brand-black text-sm font-medium">
            Franjas activas{' '}
            <span className="text-brand-gray-400 font-normal">
              ({filteredActiveSlots.length}{' '}
              {filteredActiveSlots.length === 1 ? 'franja' : 'franjas'})
            </span>
          </h3>
        </div>
        <table className="w-full">
          <thead className="bg-brand-gray-50">
            <tr>
              <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                Día
              </th>
              <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                Franja
              </th>
              <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                Max. citas
              </th>
              <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                Activo
              </th>
              <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                Uso max. día
              </th>
              <th className="text-right font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-gray-200">
            {filteredActiveSlots.map((slot) => {
              const maxUsed = getSlotUsage(slot);
              const usagePct =
                slot.max_bookings > 0
                  ? Math.round((maxUsed / slot.max_bookings) * 100)
                  : 0;
              return (
                <tr
                  key={slot.id}
                  className="hover:bg-brand-gray-50/50 transition-colors"
                >
                  <td className="px-5 py-3 font-sans text-brand-black text-sm">
                    {dayNames[slot.day_of_week]}
                  </td>
                  <td className="px-5 py-3 font-sans text-brand-gray-600 text-sm">
                    {slot.start_time} — {slot.end_time}
                  </td>
                  <td className="px-5 py-3 font-sans text-brand-black text-sm">
                    {slot.max_bookings}
                  </td>
                  <td className="px-5 py-3">
                    <button
                      onClick={() =>
                        updateSlot(slot.id, { is_active: !slot.is_active })
                      }
                      className={`w-10 h-5 rounded-full transition-colors relative ${
                        slot.is_active ? 'bg-brand-sage' : 'bg-brand-gray-300'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                          slot.is_active ? 'left-5' : 'left-0.5'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex items-center gap-1 font-sans text-xs font-medium px-2 py-0.5 rounded ${
                        usagePct >= 90
                          ? 'bg-red-50 text-red-600'
                          : usagePct >= 60
                          ? 'bg-amber-50 text-amber-600'
                          : 'bg-emerald-50 text-emerald-600'
                      }`}
                    >
                      {maxUsed} / {slot.max_bookings}
                      {usagePct >= 90 && <i className="ri-fire-line" />}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={async () => {
                          await addSlot({
                            day_of_week: slot.day_of_week,
                            start_time: slot.start_time,
                            end_time: slot.end_time,
                            is_active: slot.is_active,
                            max_bookings: slot.max_bookings,
                          });
                        }}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-sage hover:text-white transition-colors"
                        title="Duplicar"
                      >
                        <i className="ri-file-copy-line" />
                      </button>
                      <button
                        onClick={() => openEdit(slot)}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                        title="Editar"
                      >
                        <i className="ri-pencil-line" />
                      </button>
                      <button
                        onClick={() => setShowDeleteSlot(slot.id)}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredActiveSlots.length === 0 && (
          <div className="p-8 text-center font-sans text-brand-gray-400 text-sm">
            {dayFilter === 'all' && !timeFilter
              ? 'No hay franjas activas'
              : 'Ninguna franja coincide con el filtro'}
          </div>
        )}
      </div>

      {inactiveSlots.length > 0 && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 bg-brand-gray-50 border-b border-brand-gray-200">
            <h3 className="font-sans text-brand-black text-sm font-medium">
              Franjas inactivas{' '}
              <span className="text-brand-gray-400 font-normal">
                ({inactiveSlots.length}{' '}
                {inactiveSlots.length === 1 ? 'franja' : 'franjas'})
              </span>
            </h3>
          </div>
          <table className="w-full">
            <thead className="bg-brand-gray-50">
              <tr>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Día
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Franja
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Max. citas
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Uso max. día
                </th>
                <th className="text-right font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-gray-200">
              {inactiveSlots.map((slot) => {
                const maxUsed = getSlotUsage(slot);
                return (
                  <tr
                    key={slot.id}
                    className="hover:bg-brand-gray-50/50 transition-colors"
                  >
                    <td className="px-5 py-3 font-sans text-brand-gray-400 text-sm">
                      {dayNames[slot.day_of_week]}
                    </td>
                    <td className="px-5 py-3 font-sans text-brand-gray-400 text-sm">
                      {slot.start_time} — {slot.end_time}
                    </td>
                    <td className="px-5 py-3 font-sans text-brand-gray-400 text-sm">
                      {slot.max_bookings}
                    </td>
                    <td className="px-5 py-3">
                      <span className="font-sans text-xs font-medium px-2 py-0.5 rounded bg-brand-gray-100 text-brand-gray-400">
                        {maxUsed} / {slot.max_bookings}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() =>
                            updateSlot(slot.id, { is_active: true })
                          }
                          className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 hover:bg-emerald-100 transition-colors"
                          title="Reactivar"
                        >
                          <i className="ri-refresh-line" />
                        </button>
                        <button
                          onClick={() => setShowDeleteSlot(slot.id)}
                          className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors"
                          title="Eliminar"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editingSlot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-brand-black text-xl">
                Editar franja horaria
              </h3>
              <button
                onClick={() => setEditingSlot(null)}
                className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            {(() => {
              const activeCount = appointments.filter((appt) => {
                if (appt.status === 'cancelled') return false;
                if (!appt.preferred_time) return false;
                const d = new Date(appt.preferred_date);
                if (d.getDay() !== editingSlot.day_of_week) return false;
                const type = types.find(
                  (t) => t.name === appt.service_type
                );
                const duration = type?.duration_minutes || 30;
                const apptStart = timeToMinutes(appt.preferred_time);
                const apptEnd = apptStart + duration;
                const slotStart = timeToMinutes(editingSlot.start_time);
                const slotEnd = timeToMinutes(editingSlot.end_time);
                return apptStart < slotEnd && apptEnd > slotStart;
              }).length;
              if (activeCount > 0) {
                return (
                  <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 flex items-start gap-2">
                    <i className="ri-alert-line text-amber-600 text-sm mt-0.5" />
                    <div>
                      <p className="font-sans text-amber-800 text-xs font-medium">
                        {activeCount} cita{activeCount > 1 ? 's' : ''} activa
                        {activeCount > 1 ? 's' : ''} en esta franja
                      </p>
                      <p className="font-sans text-amber-600 text-xs mt-0.5">
                        Si reduces la capacidad por debajo de {activeCount}, las
                        citas existentes seguirán activas pero nuevas reservas
                        quedarán bloqueadas.
                      </p>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            <div className="space-y-4">
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                  Día de la semana
                </label>
                <select
                  value={editDay}
                  onChange={(e) => setEditDay(Number(e.target.value))}
                  className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm bg-white focus:outline-none focus:border-brand-black"
                >
                  {dayNames.map((d, i) => (
                    <option key={i} value={i}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                    Hora de inicio
                  </label>
                  <input
                    type="time"
                    value={editStart}
                    onChange={(e) => setEditStart(e.target.value)}
                    className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
                  />
                </div>
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                    Hora de fin
                  </label>
                  <input
                    type="time"
                    value={editEnd}
                    onChange={(e) => setEditEnd(e.target.value)}
                    className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
                  />
                </div>
              </div>
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                  Máximo de citas simultáneas
                </label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={editMax}
                  onChange={(e) => setEditMax(Number(e.target.value))}
                  className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
                />
                <p className="font-sans text-brand-gray-400 text-xs mt-1.5">
                  Número de citas que se pueden reservar a la vez en esta franja
                </p>
              </div>
              {editStart && editEnd && editStart >= editEnd && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2">
                  <i className="ri-alert-line text-amber-600 text-sm" />
                  <p className="font-sans text-amber-700 text-xs">
                    La hora de fin debe ser posterior a la hora de inicio
                  </p>
                </div>
              )}
              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={handleSaveEdit}
                  disabled={saving || editStart >= editEnd}
                  className="flex-1 bg-brand-black text-white font-sans text-sm py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Guardando...' : 'Guardar cambios'}
                </button>
                <button
                  onClick={() => setEditingSlot(null)}
                  className="flex-1 border border-brand-gray-200 text-brand-black font-sans text-sm py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteSlot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-600 mx-auto mb-4">
              <i className="ri-delete-bin-line text-xl" />
            </div>
            <h3 className="font-serif text-brand-black text-lg">
              Eliminar franja
            </h3>
            <p className="font-sans text-brand-gray-500 text-sm mt-2">
              Se eliminarán los horarios de disponibilidad de esta franja. Las
              citas existentes no se verán afectadas.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => setShowDeleteSlot(null)}
                className="flex-1 border border-brand-gray-200 text-brand-black font-sans text-sm py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  deleteSlot(showDeleteSlot);
                  setShowDeleteSlot(null);
                }}
                className="flex-1 bg-red-600 text-white font-sans text-sm py-2.5 rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}